import { supabase } from './supabase';

/**
 * Operacje na tokenach Meta, ktore wymagaja sekretu aplikacji.
 *
 * Front ich sam nie wykonuje - sekret aplikacji nie moze byc w bundlu, bo Vite
 * wstrzykuje kazda zmienna VITE_* w kod przegladarki. Robi je funkcja brzegowa
 * `meta-token-exchange`, ktora bierze uzytkownika z tokena sesji i sama dobiera
 * klucze: najpierw wlasne uzytkownika z `user_settings`, potem domyslne aplikacji.
 */

/** Ktory zestaw kluczy ma wziac serwer. */
export type MetaApp = 'facebook' | 'instagram' | 'instagram_direct';

export interface MetaTokenDebugData {
  is_valid?: boolean;
  expires_at?: number;
  scopes?: string[];
  [key: string]: unknown;
}

class MetaTokenError extends Error {}

interface MetaTokenExchangeResponse {
  success?: boolean;
  access_token?: string;
  expires_in?: number | null;
  data?: MetaTokenDebugData | null;
  message?: string;
  hint?: string;
  error?: string;
}

async function callMetaTokenExchange(
  payload: Record<string, unknown>,
): Promise<MetaTokenExchangeResponse> {
  const { data: { session } } = await supabase.auth.getSession();

  if (!session?.access_token) {
    throw new MetaTokenError('Musisz byc zalogowany, zeby odswiezyc token Meta');
  }

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const response = await fetch(`${supabaseUrl}/functions/v1/meta-token-exchange`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
    },
    body: JSON.stringify(payload),
  });

  const body: MetaTokenExchangeResponse | null = await response.json().catch(() => null);

  if (!response.ok || !body?.success) {
    throw new MetaTokenError(body?.message || body?.hint || body?.error || 'Operacja na tokenie Meta nie udala sie');
  }

  return body;
}

/**
 * Wymienia krotkozyciowy token z `FB.login` na dlugozyciowy.
 * Token nalezy do uzytkownika i jego przegladarka i tak go ma - sekretem jest
 * wylacznie App Secret, ktory zostaje na serwerze.
 */
export async function exchangeForLongLivedToken(token: string, app: MetaApp = 'facebook'): Promise<string> {
  const body = await callMetaTokenExchange({ action: 'exchange_long_lived', app, token });
  if (!body.access_token) {
    throw new MetaTokenError('Serwer nie zwrocil tokena Meta');
  }
  return body.access_token;
}

/** `debug_token` - wymaga tokena dostepu aplikacji, czyli `appId|appSecret`. */
export async function debugMetaToken(token: string, app: MetaApp = 'facebook'): Promise<MetaTokenDebugData | null> {
  const body = await callMetaTokenExchange({ action: 'debug_token', app, token });
  return body.data ?? null;
}

/** Token strony pobrany tokenem aplikacji. `pageId` to id strony w Graph API. */
export async function fetchPageAccessToken(pageId: string, app: MetaApp = 'facebook'): Promise<string> {
  const body = await callMetaTokenExchange({ action: 'page_token', app, pageId });
  if (!body.access_token) {
    throw new MetaTokenError('Serwer nie zwrocil tokena strony');
  }
  return body.access_token;
}
